import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { FormLayout } from '../components/FormLayout';
import { PhotoGalleryField } from '../components/PhotoGalleryField';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { BazaarItem, SingleResponse } from '../api/types';

export function NewBazaarItem() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [priceEuros, setPriceEuros] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('title', title);
      if (description) form.append('description', description);
      form.append('external_url', externalUrl);
      if (priceEuros) form.append('price_cents', String(Math.round(parseFloat(priceEuros) * 100)));
      form.append('currency', 'EUR');
      photos.forEach((file) => form.append('photos[]', file));

      await apiFetch<SingleResponse<BazaarItem>>('/api/v1/bazaar-items', {
        method: 'POST',
        body: form,
      });
      navigate('/bazaar');
    } catch (err: any) {
      setError(err.message ?? t('newBazaar.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormLayout
        header={<PageHeader />}
        title={t('newBazaar.title')}
        error={error || null}
        form={
          <>
            <Alert severity="info">{t('newBazaar.info')}</Alert>

            <TextField
              label={t('newBazaar.itemTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label={t('newBazaar.link')}
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              required
              fullWidth
              placeholder="https://www.ebay.com/itm/..."
            />

            <TextField
              label={t('newBazaar.price')}
              type="number"
              value={priceEuros}
              onChange={(e) => setPriceEuros(e.target.value)}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </>
        }
        aside={
          <Stack spacing={3}>
            <MarkdownEditor
              label={t('newBazaar.description')}
              value={description}
              onChange={setDescription}
              rows={6}
            />
            <PhotoGalleryField
              label={t('newBazaar.addPhotos')}
              addButtonLabel={t('newBazaar.addPhotos')}
              newPhotos={photos}
              onAdd={(files) => setPhotos((prev) => [...prev, ...files])}
              onRemoveNew={(idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
            />
          </Stack>
        }
        footer={
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? t('common.uploading') : t('common.submit')}
            </Button>
          </Stack>
        }
      />
    </Box>
  );
}
